/**
 * WS chat transport — a thin WebSocket wrapper that parses frames into WsChatEvents.
 * Keeps the raw socket out of React; the caller just consumes onEvent + send().
 *
 * The socket is multiplexed: one connection carries every conversation, with
 * frames routed by a `sessionId` field in both directions (see manager.ts).
 */
import {
  createToolCallChunkFilter,
  parseFrameWithSession,
  type WsChatEvent,
} from './protocol';

export interface WsChatConnection {
  /** Send a user message routed to one conversation. */
  send: (text: string, sessionId?: string, agentType?: string, scanKeywords?: string[]) => void;
  /** Send an arbitrary frame (e.g. an approval response). */
  sendRaw: (frame: Record<string, unknown>) => void;
  close: () => void;
  isOpen: () => boolean;
  /**
   * Timestamp (ms) of the last frame RECEIVED on this socket. On a half-open
   * socket the receive side goes dead while `isOpen()` (readyState===OPEN) and
   * `sendRaw` keep succeeding — so this is the only reliable liveness signal.
   * The manager's heartbeat uses it to detect a stalled turn and reconnect.
   */
  lastFrameAt: () => number;
}

/**
 * How long a socket may stay in CONNECTING before it is given up on (10-26, kiechee, Oscar).
 *
 * A browser WebSocket has no opening-handshake timeout of its own: a socket whose handshake
 * never completes stays CONNECTING with no `open`, `error` or `close` — and the manager, which
 * awaits this promise before sending, waited with it. The merchant's first message of a new
 * chat sat under "Oscar is thinking" with the composer locked, and never left the browser
 * (chatbot-api saw the socket arrive 14s after the bootstrap, then only an EOF). Past this the
 * socket is closed and the promise rejects, so the manager's own retry / failure path runs.
 *
 * Kept well above the slowest open measured on dev (14-15s from bootstrap to CLIENT_CONNECTED
 * that day) so a slow-but-healthy connect is never cut short.
 */
export const WS_OPEN_TIMEOUT_MS = 25_000;

export interface WsChatConnectionHandlers {
  url: string;
  traceId?: string;
  /** Give up on a socket still CONNECTING after this long (default WS_OPEN_TIMEOUT_MS). */
  openTimeoutMs?: number;
  /** sessionId is the routing field stamped by chatbot-api (null from a pre-multiplex backend). */
  onEvent: (event: WsChatEvent, sessionId: string | null) => void;
  onOpen?: () => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
}

/**
 * Open a WS chat socket. Resolves once the socket is OPEN (so the caller can
 * await readiness); rejects if it errors/closes before opening.
 */
export function openWsChatConnection(h: WsChatConnectionHandlers): Promise<WsChatConnection> {
  return new Promise((resolve, reject) => {
    const ws = new WebSocket(h.url);
    const filterToolCallChunks = createToolCallChunkFilter();
    let opened = false;
    let lastFrameAt = Date.now();
    const startedAt = Date.now();
    const baseLog = () => ({
      traceId: h.traceId,
      elapsedMs: Date.now() - startedAt,
      readyState: ws.readyState,
      url: h.url,
    });

    console.info('[WS chat] WS_CONNECT_START', baseLog());

    const openTimeoutMs = h.openTimeoutMs ?? WS_OPEN_TIMEOUT_MS;
    const openTimer = setTimeout(() => {
      if (opened) return;
      console.warn('[WS chat] WS_OPEN_TIMEOUT', { ...baseLog(), openTimeoutMs });
      // Detach first: this attempt is over, and a late open/close must not reach the caller.
      ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
      try {
        ws.close();
      } catch {
        /* noop */
      }
      const trace = h.traceId ? ` traceId=${h.traceId}` : '';
      reject(new Error(`WS chat WebSocket did not open within ${openTimeoutMs}ms${trace}`));
    }, openTimeoutMs);

    const connection: WsChatConnection = {
      send: (text: string, sessionId?: string, agentType?: string, scanKeywords?: string[]) => {
        if (ws.readyState === WebSocket.OPEN) {
          const frame: Record<string, unknown> = { type: 'message', content: text, sessionId, agentType };
          if (scanKeywords && scanKeywords.length) frame.scanKeywords = scanKeywords;
          ws.send(JSON.stringify(frame));
        }
      },
      sendRaw: (frame: Record<string, unknown>) => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify(frame));
        }
      },
      close: () => {
        ws.onopen = ws.onmessage = ws.onerror = ws.onclose = null;
        try {
          ws.close();
        } catch {
          /* noop */
        }
      },
      isOpen: () => ws.readyState === WebSocket.OPEN,
      lastFrameAt: () => lastFrameAt,
    };

    ws.onopen = () => {
      clearTimeout(openTimer);
      opened = true;
      lastFrameAt = Date.now();
      console.info('[WS chat] WS_OPEN', baseLog());
      h.onOpen?.();
      resolve(connection);
    };
    ws.onmessage = evt => {
      // Any inbound frame proves the receive side is alive (there's no pong
      // frame, so "last frame received" is the liveness clock).
      lastFrameAt = Date.now();
      const raw = typeof evt.data === 'string' ? evt.data : '';
      logDiagnosticFrame(raw, h.traceId);
      const { sessionId, event } = parseFrameWithSession(raw);
      const visibleEvent = event ? filterToolCallChunks(event, sessionId) : null;
      if (visibleEvent) h.onEvent(visibleEvent, sessionId);
    };
    ws.onerror = event => {
      if (!opened) clearTimeout(openTimer);
      console.warn('[WS chat] WS_ERROR', baseLog());
      h.onError?.(event);
      if (!opened) reject(new Error('WS chat WebSocket failed to connect'));
    };
    ws.onclose = event => {
      clearTimeout(openTimer);
      const payload = {
        ...baseLog(),
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      };
      if (event.wasClean && event.code === 1000) {
        console.info('[WS chat] WS_CLOSE', payload);
      } else {
        console.warn('[WS chat] WS_CLOSE', payload);
      }
      h.onClose?.(event);
      if (!opened) {
        const reason = event.reason ? ` ${event.reason}` : '';
        const trace = h.traceId ? ` traceId=${h.traceId}` : '';
        reject(new Error(`WS chat WebSocket closed before opening (${event.code}${reason})${trace}`));
      }
    };
  });
}

function logDiagnosticFrame(raw: string, traceId?: string): void {
  if (!raw) return;
  try {
    const frame = JSON.parse(raw) as { type?: string; [key: string]: unknown };
    if (frame.type === 'diagnostic') {
      console.warn('[WS chat] WS_DIAGNOSTIC', { traceId, ...frame });
    }
  } catch {
    return;
  }
}
